import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import RightSideMenuProvider from "components/RightSideMenuProvider";
import RightSideMenuDealer from "components/RightSideMenuDealer";
import RightSideMenuAdmin from "components/RightSideMenuAdmin";

import MyAdsProvider from "components/MyAdsProvider";
import MyAdsDealer from "components/MyAdsDealer";
import DashboardMenu from "components/DashboardMenu";
import ProjectRoutes from "./Routes";
import LeftMenuNotificationsDealerDashboard from "components/LeftMenuNotificationsDealerDashboard";
import MyOffersCountersDealer from "components/MyOffersCountersDealer";
import NotificationsBell from "components/NotificationsBell";
import { useTour, WelcomeModal, TourProvider, syncOnboardingStateFromServer } from "./onboarding";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EMPTY_COUNTERS = { all: 0, noOffers: 0, dismantling: 0, rehabilitation: 0, pending: 0, approved: 0, archive: 0 };

const EMPTY_DEALER_COUNTERS = {
  ads_all: 0,
  ads_new: 0,
  ads_archive: 0,
  ads_pending: 0,
  dismantling: 0,
  rehabilitation: 0,
  offers_new: 0,
  offers_approved: 0,
  cars_auction: 0,
  bundles_auction: 0,
};

function safeJSONParse(str, fallback) {
  try {
    return str ? JSON.parse(str) : fallback;
  } catch {
    return fallback;
  }
}

function AppInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const { startCatalogTour } = useTour();

  const isRootPath = location.pathname === "/";
  const isPaymeBridgePath = location.pathname.startsWith("/payme/");
  const isDealerCatalogPath = location.pathname === "/dealer-cars";

  const handleStartTour = React.useCallback(() => {
    if (!isDealerCatalogPath) {
      navigate("/dealer-cars");
      // Give the page time to mount before starting the tour
      setTimeout(startCatalogTour, 600);
    } else {
      startCatalogTour();
    }
  }, [isDealerCatalogPath, navigate, startCatalogTour]);

  const hideLeftMenu = useMemo(() => {
    return (
      location.pathname.startsWith("/recived-offers") ||
      location.pathname === "/dashboard-dealer" ||
      location.pathname === "/dashboard-provider" ||
      location.pathname === "/my-offers" ||
      location.pathname === "/dealer-accounts" ||
      location.pathname === "/supplier-accounts" ||
      /^\/dashboard-provider\/\d+$/.test(location.pathname) ||
      /^\/(provider-cars|dealer-cars)\/\d+$/.test(location.pathname)
    );
  }, [location.pathname]);

  const [adCounters, setAdCounters] = useState(EMPTY_COUNTERS);

  const [dealerCounters, setDealerCounters] = useState(() => {
    const stored = localStorage.getItem("dealerCounters");
    return safeJSONParse(stored, EMPTY_DEALER_COUNTERS);
  });

  const [currentUser, setCurrentUser] = useState(() => {
    const stored = localStorage.getItem("currentUser");
    return safeJSONParse(stored, null);
  });

  // ✅ מצב sync מהשרת: false = ממתין לתשובה, true = הושלם (גם במקרה של כשל)
  const [onboardingSyncReady, setOnboardingSyncReady] = useState(() => {
    // אם אין משתמש מחובר בטעינה ראשונית — אין צורך להמתין
    const stored = localStorage.getItem("currentUser");
    const u = safeJSONParse(stored, null);
    return !u?.id;
  });
  const [onboardingServerState, setOnboardingServerState] = useState(() => {
    const stored = localStorage.getItem("currentUser");
    const u = safeJSONParse(stored, null);
    return {
      userId: u?.id ?? null,
      loaded: !u?.id,
      welcomeSeen: true,
    };
  });

  useEffect(() => {
    localStorage.setItem("dealerCounters", JSON.stringify(dealerCounters));
  }, [dealerCounters]);

  useEffect(() => {
    if (currentUser) localStorage.setItem("currentUser", JSON.stringify(currentUser));
    else localStorage.removeItem("currentUser");
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.id) {
      setOnboardingSyncReady(true);
      setOnboardingServerState({
        userId: null,
        loaded: true,
        welcomeSeen: true,
      });
      return;
    }
    // ✅ מאפסים למצב "ממתין" בכל פעם שמשתמש מתחבר, ורק אחרי שהשרת חוזר מסמנים "סיום"
    setOnboardingSyncReady(false);
    setOnboardingServerState({
      userId: currentUser.id,
      loaded: false,
      welcomeSeen: true,
    });

    let isActive = true;
    syncOnboardingStateFromServer(currentUser.id).then((result) => {
      if (!isActive) return;
      setOnboardingServerState({
        userId: currentUser.id,
        loaded: true,
        welcomeSeen: result?.status === "success" ? result.welcomeSeen : true,
      });
      setOnboardingSyncReady(true);
    });

    return () => {
      isActive = false;
    };
  }, [currentUser?.id]);

  const handleLogout = () => {
    localStorage.removeItem("currentUser");
    localStorage.removeItem("userName");
    setCurrentUser(null);
    navigate("/", { replace: true });
  };

  const userType = Number(currentUser?.user_type);

  return (
    <>
      <ProjectRoutes
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
        setGlobalCounters={setAdCounters}
        setDealerCounters={setDealerCounters}
      />

      {currentUser && userType === 3 && !isRootPath && !isPaymeBridgePath && (
        <WelcomeModal
          userId={currentUser.id}
          syncReady={onboardingSyncReady}
          showWelcome={
            onboardingServerState.userId === currentUser.id &&
            onboardingServerState.loaded &&
            !onboardingServerState.welcomeSeen
          }
          canStartCatalogTour={true}
          onStartTour={handleStartTour}
        />
      )}

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        rtl
      />

      {/* 🔔 פעמון – לא בדף לוגין */}
      {!isRootPath &&
        !isPaymeBridgePath &&
        currentUser &&
        (userType === 2 || userType === 3) && (
          <NotificationsBell currentUser={currentUser} />
        )}

      {/* כל הסיידברים לא בדף לוגין */}
      {!isRootPath && !isPaymeBridgePath && (
        <>
          {userType === 2 ? (
            <RightSideMenuProvider handleLogout={handleLogout} currentUser={currentUser} />
          ) : userType === 3 ? (
            <RightSideMenuDealer handleLogout={handleLogout} currentUser={currentUser} />
          ) : null}

          {!hideLeftMenu && currentUser && (
            userType === 2 ? (
              <MyAdsProvider counters={adCounters} />
            ) : userType === 3 ? (
              <MyAdsDealer counters={dealerCounters} />
            ) : null
          )}

          {userType === 3 && location.pathname.includes("/my-offers") && (
            <MyOffersCountersDealer counters={dealerCounters} />
          )}

          {location.pathname.includes("/dashboard-dealer") && currentUser && (
            <>
              <DashboardMenu currentUser={currentUser} counters={dealerCounters} />
              <LeftMenuNotificationsDealerDashboard currentUser={currentUser} />
            </>
          )}

          {/* Admin Sidebar */}
          {currentUser?.user_type === 1 && (
            <RightSideMenuAdmin currentUser={currentUser} handleLogout={handleLogout} />
          )}
        </>
      )}
    </>
  );
}

function App() {
  const initUser = safeJSONParse(localStorage.getItem("currentUser"), null);
  return (
    <TourProvider userId={initUser?.id}>
      <AppInner />
    </TourProvider>
  );
}

export default App;
